import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { buildPhaseModel, phaseForStep, parseComparisonStep } from "./phaseModel";
import { useTopikMotion } from "../topikMotion";

// The per-phase context line (§10.2) - replaces the banned "Step 3 of 17" altitude with
// phase-local wording ("Harmful claims · Pair 2 · More common").
const contextLine = (t, step) => {
  const phase = phaseForStep(step);
  if (!phase) return null;

  const phaseLabel = t(`topik-progress.phases.${phase}`);
  const comparison = parseComparisonStep(step);
  if (comparison) {
    const slot = t(`topik-progress.slots.${comparison.slot}`);
    const question = t(`topik-progress.questions.${comparison.question}`);
    return `${phaseLabel} · ${slot} · ${question}`;
  }
  return phaseLabel;
};

// Persistent phase rail rendered above every step view (adopts Janne's segmented-bar visual
// language + ARIA pattern, re-aimed onto our phase model). Tapping it opens the journey map.
const TopikProgressRail = ({ t, steps, step, mapOpen, onOpenMap }) => {
  const { reduce } = useTopikMotion();
  const groups = buildPhaseModel(steps, step);
  const currentIndex = steps.indexOf(step);
  const context = contextLine(t, step);

  // Suppress the advance emphasis on the very first mount (§1.2-5d "no sweep on initial
  // mount") - it should fire only when the step actually advances thereafter.
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; }, []);
  const emphasize = mounted.current && !reduce;

  const renderSegment = (segment) => {
    if (segment.state === "current") {
      // Keyed on `step` so it remounts (and replays the one-beat emphasis) on each advance.
      // The neighbouring done/upcoming segments cross-fade via the scss `transition` (which
      // also covers a backtrack un-fill), so all three states of advance are movement.
      return (
        <motion.span
          key={step}
          className="topik-rail__segment topik-rail__segment--current"
          initial={emphasize ? { scaleY: 2.4, opacity: 0.6 } : false}
          animate={{ scaleY: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      );
    }
    return (
      <span
        key={segment.name}
        className={`topik-rail__segment topik-rail__segment--${segment.state}`}
      />
    );
  };

  // Content inside a button is never read on its own (the progressbar included), so the
  // progress and context line reach screen readers as the button's description instead.
  const progressDescription = [
    `${t("topik-progress.aria-progress")} ${currentIndex + 1} / ${steps.length}`,
    context,
  ].filter(Boolean).join(". ");

  return (
    <button
      type="button"
      className="topik-rail"
      onClick={onOpenMap}
      aria-expanded={mapOpen}
      aria-label={t("topik-progress.open-map")}
      aria-describedby="topik-rail-progress-description"
    >
      <span id="topik-rail-progress-description" className="visually-hidden">{progressDescription}</span>
      <div
        className="topik-rail__phases"
        role="progressbar"
        aria-valuenow={currentIndex + 1}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-label={context || t("topik-progress.aria-progress")}
      >
        {groups.map((group) => (
          <div
            key={group.key}
            className={`topik-rail__phase topik-rail__phase--${group.key} topik-rail__phase--${group.state}`}
          >
            <span className="topik-rail__phase-label">
              <span
                className={`topik-rail__phase-icon topik-rail__phase-icon--${group.meta.polarity || "neutral"}`}
                aria-hidden="true"
              >
                {group.meta.icon}
              </span>
              <span className="topik-rail__phase-name">{t(`topik-progress.phases.${group.key}`)}</span>
            </span>
            <span className="topik-rail__segments">
              {group.steps.map(renderSegment)}
            </span>
          </div>
        ))}
      </div>
      {context && <p className="topik-rail__context">{context}</p>}
    </button>
  );
};

TopikProgressRail.propTypes = {
  t: PropTypes.func.isRequired,
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  step: PropTypes.string.isRequired,
  mapOpen: PropTypes.bool,
  onOpenMap: PropTypes.func.isRequired,
};

TopikProgressRail.defaultProps = {
  mapOpen: false,
};

export default withTranslation("views")(TopikProgressRail);
